import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { EditableSummary } from '@components/ui/editable-summary';
import { SearchableSelect } from '@components/ui/searchable-select';
import { ChevronDown, ChevronUp, Edit3 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import type { SelectOption } from '@/types/formSchema';

interface CaseDispositionAccordionProps {
  currentDispositionCode?: number;
  dispositionOptions: SelectOption[];
  fullSummary?: string;
  isLoadingDispositions: boolean;
  isUpdating?: boolean;
  onDispositionChange: (code: number | '') => void;
  expandedItems: string[];
  onExpandedItemsChange: (items: string[]) => void;
  canEdit?: boolean;
  canEditFullSummary?: boolean;
  isEditingFullSummary?: boolean;
  onFullSummaryEdit?: () => void;
  onFullSummarySave?: (newSummary: string) => void;
  onFullSummaryCancel?: () => void;
  isLoadingFullSummary?: boolean;
  isSummaryStale?: boolean;
}

export const CaseDispositionAccordion: React.FC<CaseDispositionAccordionProps> = ({
  currentDispositionCode,
  dispositionOptions,
  fullSummary,
  isLoadingDispositions,
  isUpdating = false,
  onDispositionChange,
  expandedItems,
  onExpandedItemsChange,
  canEdit = true,
  canEditFullSummary = false,
  isEditingFullSummary = false,
  onFullSummaryEdit,
  onFullSummarySave,
  onFullSummaryCancel,
  isLoadingFullSummary = false,
  isSummaryStale = false,
}) => {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  // Detect if summary text is truncated
  useEffect(() => {
    const element = summaryRef.current;
    if (element && !isSummaryExpanded) {
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [fullSummary, isSummaryExpanded]);

  return (
    <Accordion
      type="multiple"
      value={expandedItems}
      onValueChange={onExpandedItemsChange}
      className="space-y-3"
    >
      <AccordionItem value="case-disposition" className="bg-card border rounded-lg">
        {/* Only the title lives inside the trigger (a <button>): text inside a button
            can't be drag-selected, so the summary renders below it as plain text that
            users can select and copy (BV-2221). */}
        <AccordionTrigger
          className="px-3 py-3 text-sm font-semibold text-foreground hover:bg-muted rounded-t-lg gap-2"
          action={
            fullSummary &&
            !isEditingFullSummary &&
            canEditFullSummary &&
            onFullSummaryEdit && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onFullSummaryEdit}
                className="h-6 px-2 mr-1 text-muted-foreground hover:text-foreground"
                title="Edit summary"
                aria-label="Edit summary"
              >
                <Edit3 className="w-3 h-3" />
              </Button>
            )
          }
        >
          <div className="flex-1 text-left">Case Details</div>
        </AccordionTrigger>
        <div className="px-3 pb-3 -mt-2">
          {fullSummary && !isEditingFullSummary && (
            <div>
              <div
                ref={summaryRef}
                className={`text-xs font-normal text-muted-foreground ${isSummaryExpanded ? '' : 'line-clamp-2'}`}
              >
                {fullSummary}
              </div>
              {isTruncated && (
                <button
                  type="button"
                  onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1 flex items-center gap-1"
                >
                  {isSummaryExpanded ? (
                    <>
                      Show less <ChevronUp className="w-3 h-3" />
                    </>
                  ) : (
                    <>
                      Show more <ChevronDown className="w-3 h-3" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          {isEditingFullSummary && onFullSummarySave && onFullSummaryCancel && (
            <div className="mt-1">
              <EditableSummary
                value={fullSummary || ''}
                onSave={onFullSummarySave}
                onCancel={onFullSummaryCancel}
                isLoading={isLoadingFullSummary}
                placeholder="Enter case summary..."
              />
            </div>
          )}
          {!fullSummary && !isEditingFullSummary && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-normal text-muted-foreground italic">
                No case summary available
              </span>
              {canEditFullSummary && onFullSummaryEdit && (
                <button
                  type="button"
                  onClick={onFullSummaryEdit}
                  className="flex items-center gap-1 text-xs font-medium text-foreground hover:text-primary transition-colors cursor-pointer"
                >
                  <Edit3 className="w-3 h-3" />
                  Add Summary
                </button>
              )}
            </div>
          )}
          {isSummaryStale && !isEditingFullSummary && (
            <div className="mt-1 text-xs font-normal text-amber-600">
              Fields updated since last summary — regenerate for latest summary
            </div>
          )}
        </div>
        <AccordionContent className="px-3 pb-3">
          <div className="group px-2 py-2 -mx-2 rounded hover:bg-muted transition-colors">
            <label className="text-xs text-muted-foreground mb-1 block">Case Disposition</label>
            <SearchableSelect
              value={currentDispositionCode ?? ''}
              onValueChange={value => onDispositionChange(value as number | '')}
              options={dispositionOptions}
              placeholder={
                isLoadingDispositions
                  ? 'Loading...'
                  : isUpdating
                    ? 'Updating...'
                    : 'Select disposition...'
              }
              disabled={isLoadingDispositions || isUpdating || !canEdit}
              searchPlaceholder="Search dispositions..."
              emptyText="No dispositions found"
              className="h-8 text-sm"
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
